import axios from "axios";
import get from "lodash/get";
import { getTimeZoneDetails } from "utils/dates";
import TokenManager from "utils/TokenManager";
import moment from "moment";
import { dataCheck, transformURL, urlCheck } from "containers/login/utils";
import { getActiveUserManager } from "utils/oidcUserManager";
import { default as Configs } from "../constants/Configs";
import { BASE_URL, MAX_RETRY, RETRY_INTERVAL } from "./constants";

const https = window.cy
  ? {}
  : typeof window !== "undefined"
    ? {}
    : await import("https");
let API_IN_PROGRESS = "";

// At request level
const agent = window.cy
  ? {}
  : typeof window !== "undefined"
    ? {}
    : new https.default.Agent({
        rejectUnauthorized: false,
      });

const clientCurrency = TokenManager.getClientCurrencyCode();
const corpEntityCurrency = TokenManager.getCurrencyCode();
const isEntitySelected = TokenManager.isCorpEntityRelatedToUser();

export const isDifferentCurrency =
  isEntitySelected &&
  corpEntityCurrency &&
  clientCurrency !== corpEntityCurrency;

let cancelAPI = axios.CancelToken.source();

export const regenerateCancelToken = () => {
  cancelAPI.cancel();
  cancelAPI = axios.CancelToken.source();
};

const ConvertHeadersForBackend = (headers) => {
  headers["todays_date"] = moment().format("MM/DD/YYYY");
  if (get(getTimeZoneDetails(), "name", "")) {
    headers["timezone"] = get(getTimeZoneDetails(), "name", "");
  }
  if (get(getTimeZoneDetails(), "offset", "")) {
    headers["time_zone"] = get(getTimeZoneDetails(), "offset", "");
  }
  headers["Content-Type"] = "application/json";
  // headers["Access-Control-Allow-Origin"] = "*";
  if (isDifferentCurrency) {
    headers["use_foreign_keys"] = 1;
  }
  return headers;
};

const retryRequest = (originalRequest) => {
  if (API_IN_PROGRESS === originalRequest.url) {
    return null;
  }
  API_IN_PROGRESS = originalRequest.url;
  return new Promise((resolve) => {
    const updatedOriginalRequest = originalRequest.data
      ? {
          ...originalRequest,
          data: JSON.parse(get(originalRequest, "data", "{}")),
        }
      : originalRequest;
    setTimeout(() => {
      resolve(instanceWithRetry(updatedOriginalRequest));
    }, RETRY_INTERVAL);
  });
};

/* ************************
 * Axios instances
 ****************************/

export const instance = axios.create({
  baseURL: BASE_URL,
  transformRequest: [
    (data, headers) => {
      ConvertHeadersForBackend(headers);
      return dataCheck(data);
    },
  ],
});

// declare a request interceptor
instance.interceptors.request.use(
  async (config) => {
    const { url } = config;
    config.url = urlCheck(url);
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();

    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken?.access_token || ""}`;
        config.headers["Content-Type"] = "Application/json";
      }
    } else {
      if (!get(config, "auth", "")) {
        config.auth = {
          username: TokenManager.get(),
          password: Math.random().toString(20),
        };
      }
    }
    config.cancelToken = cancelAPI.token;
    return config;
  },
  (error) =>
    // handle the error

    Promise.reject(error)
);

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      return Promise.reject({
        ...error.response.data,
        status: error.response.status,
      });
    }

    return Promise.reject(error);
  }
);

export const downloadFile = (url, name) => {
  let urlExt = url.split("/");
  urlExt = urlExt[urlExt.length - 1];
  instance
    .post("url", { url }, { responseType: "blob" })
    .then((result) => {
      const newurl = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement("a");
      link.href = newurl;
      link.setAttribute("download", name || urlExt);
      document.body.appendChild(link);
      link.click();
    })
    .catch(() => {});
};

/* ************************
 * Instance with retry axios instances
 ****************************/

export const instanceWithRetry = axios.create({
  baseURL: BASE_URL,
  transformRequest: [
    (data, headers) => {
      ConvertHeadersForBackend(headers);
      const res = dataCheck(data);
      return res;
    },
  ],
});

// declare a request interceptor
instanceWithRetry.interceptors.request.use(
  async (config) => {
    const { url } = config;

    config.url = urlCheck(url);
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();
    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken?.access_token || ""}`;
        config.headers["Content-Type"] = "Application/json";
      }
    } else {
      if (!get(config, "auth", "")) {
        config.auth = {
          username: TokenManager.get(),
          password: Math.random().toString(20),
        };
      }
    }

    config.cancelToken = cancelAPI.token;
    return config;
  },
  (error) =>
    // handle the error

    Promise.reject(error)
);

instanceWithRetry.interceptors.response.use(
  (response) => response,
  (error) => {
    const { config } = error;
    const originalRequest = config;
    // eslint-disable-next-line no-empty
    try {
    } catch (e) {
      /* empty */
    }
    if (
      !get(error, "response.status", "") &&
      originalRequest &&
      (originalRequest.retry || 1) < MAX_RETRY
    ) {
      return retryRequest({
        ...originalRequest,
        retry: originalRequest.retry ? originalRequest.retry + 1 : 1,
      });
    } else {
      if (error.response) {
        return Promise.reject({
          ...error.response.data,
          status: error.response.status,
        });
      }

      return Promise.reject(error);
    }
  }
);

/* ************************
 * Multipart axios instances
 ****************************/

export const instanceMultipart = axios.create({
  baseURL: BASE_URL,
  httpsAgent: agent,
  transformRequest: [
    (data, headers) => {
      ConvertHeadersForBackend(headers);
      // Opt-in (via the "X-Auto-Boundary" request header): let the browser set
      // Content-Type together with the multipart boundary. Forcing the header
      // strips the boundary and breaks strict multipart parsers (FastAPI 422).
      // Callers that don't set this header keep the existing forced header, so
      // their behaviour is unchanged.
      const autoBoundary =
        typeof headers.get === "function"
          ? headers.get("X-Auto-Boundary")
          : headers["X-Auto-Boundary"];
      if (autoBoundary) {
        if (typeof headers.delete === "function") {
          headers.delete("X-Auto-Boundary");
          headers.delete("Content-Type");
        } else {
          delete headers["X-Auto-Boundary"];
          delete headers["Content-Type"];
        }
      } else {
        headers["Content-Type"] = "multipart/form-data";
      }
      headers["httpsAgent"] = agent;
      return data;
    },
  ],
});

// declare a request interceptor
instanceMultipart.interceptors.request.use(
  async (config) => {
    config.url = transformURL(config.url);
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();

    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken?.access_token || ""}`;
        config.headers["Content-Type"] = "Application/json";
      }
    } else {
      if (!get(config, "auth", "")) {
        config.auth = {
          username: TokenManager.get(),
          password: Math.random().toString(20),
        };
      }
    }
    config["httpsAgent"] = agent;
    return config;
  },
  (error) =>
    // handle the error
    Promise.reject(error)
);

/* ************************
 * Cash App axios instances
 ****************************/

export const instanceWithRetryForCashApp = axios.create({
  baseURL: Configs.CASHAPP_API_URL,
  transformRequest: [
    (data, headers) => {
      ConvertHeadersForBackend(headers);
      const res = dataCheck(data);
      return res;
    },
  ],
});

// declare a request interceptor
instanceWithRetryForCashApp.interceptors.request.use(
  async (config) => {
    const { url } = config;
    config.url = urlCheck(url);
    config.url = transformURL(config.url);
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();

    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken?.access_token || ""}`;
        config.headers["Content-Type"] = "Application/json";
      }
    } else {
      if (!get(config, "auth", "")) {
        config.auth = {
          username: TokenManager.get(),
          password: Math.random().toString(20),
        };
      }
    }
    config.cancelToken = cancelAPI.token;
    return config;
  },
  (error) =>
    // handle the error

    Promise.reject(error)
);

export const instanceMultipartForCashApp = axios.create({
  baseURL: Configs.CASHAPP_API_URL,
  httpsAgent: agent,
  transformRequest: [
    (data, headers) => {
      ConvertHeadersForBackend(headers);
      headers["Content-Type"] = "multipart/form-data";
      headers["httpsAgent"] = agent;
      return data;
    },
  ],
});

// declare a request interceptor
instanceMultipartForCashApp.interceptors.request.use(
  async (config) => {
    config.url = transformURL(config.url);
    config.url = transformURL(config.url);
    const isOAuthEnabled = TokenManager.isPingAuthorizationEnabled();

    if (isOAuthEnabled) {
      const accessToken = await getActiveUserManager().getUser();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken?.access_token || ""}`;
        config.headers["Content-Type"] = "Application/json";
      }
    } else {
      if (!get(config, "auth", "")) {
        config.auth = {
          username: TokenManager.get(),
          password: Math.random().toString(20),
        };
      }
    }
    config["httpsAgent"] = agent;
    return config;
  },
  (error) =>
    // handle the error
    Promise.reject(error)
);
